/**
 * 三選一「重抽(reroll)」純邏輯 — ROGUELITE_ROADMAP P0「重抽消費」。
 *
 * 背景:`commander.ts` 已把 `qol.rerollCharges`(指揮官等級發放、封頂 QOL_CAPS.rerollCharges=2)
 * 存進 progress,但場內三選一從沒消費它 → 死獎勵。這支提供「本場重抽預算」的純函式,
 * 由 horizontal-battle-scene 載入當本場計數、upgrade-picker 消費。
 *
 * 鐵則(玩遊戲軌 · 廣度非戰力):
 *   - 只讀 `qol.rerollCharges`(已封頂 QoL 便利),**絕不**寫 factionXP/freeXP/skillLevels/傷害/HP。
 *   - 重抽只「重洗三張」,不增加總拿牌數、不改任何卡的效果 →「更多選擇」非「更容易贏」。
 *   - 本場計數,跨場歸零(scene 重啟重新 init,從不回寫 progress)。
 *
 * 純函式 / 無 Phaser / 無 DOM 依賴 → 可單測。
 */
import { QOL_CAPS } from '../progression/commander';

/** progress.qol 的最小形狀(只讀 rerollCharges;避免耦合完整 Progress 型別)。 */
export interface RerollQolLike {
  rerollCharges?: number | null;
}

/**
 * 本場重抽次數初值 — 讀 `qol.rerollCharges`,clamp 進 [0, QOL_CAPS.rerollCharges]。
 * qol 缺/非法/負 → 0。永遠不超過硬封頂(即使存檔被竄改)。
 */
export function initialRerollCharges(qol: RerollQolLike | null | undefined): number {
  const raw = qol?.rerollCharges;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return 0;
  return Math.min(Math.floor(raw), QOL_CAPS.rerollCharges);
}

/** consume 結果:剩餘次數 + 這次有沒有成功扣到(remaining>0 才成功)。 */
export interface RerollConsumeResult {
  remaining: number;
  ok: boolean;
}

/**
 * 消費一次重抽。remaining>0 → 扣 1 並回 ok=true;remaining≤0 → 維持 0、ok=false。
 * 永不為負。純函式(不持有狀態,caller 拿回傳的 remaining 當新狀態)。
 */
export function consumeReroll(remaining: number): RerollConsumeResult {
  if (!Number.isFinite(remaining) || remaining <= 0) return { remaining: 0, ok: false };
  return { remaining: Math.floor(remaining) - 1, ok: true };
}

/** 還能不能重抽(剩餘 > 0)。 */
export function canReroll(remaining: number): boolean {
  return Number.isFinite(remaining) && remaining > 0;
}
